import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
//mui
import {
  Avatar,
  CircularProgress,
  Grid2,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
//store
import { Actions } from "../../../../redux/constants/actionTypes";
import { getStore } from "../../../../Utils/storeUtils";
import AuthContext from "../../../../store/auth-context";
//local imports
import Notifications from "./Notifications/Notifications.jsx";
import styles from "./NavbarStyles.module.scss";
//icons
import { ReactComponent as Logo } from "../../../../GlobalAssets/svg/Logo.svg";
import {
  LogoutOutlined as LogoutOutlinedIcon,
  LightModeOutlined as LightModeOutlinedIcon,
  DarkModeOutlined as DarkModeOutlinedIcon,
} from "@mui/icons-material";
import { ReactComponent as SearchIcon } from "../../../../GlobalAssets/svg/search.svg";
import ClearIcon from "@mui/icons-material/Clear";
import Lottie from "react-lottie";
import Hello from "../../../../GlobalAssets/Animations/Hello.json";
import { useEmailId } from "../../../../Utils/utils";
//styling

function NavBar({
  showLogo = true,
  showLogout = true,
  showNotifications = true,
  showProfile = true,
  search,
  setSearch,
  value,
  setValue,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const searchRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:1029px)");
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });
  const [loading, setLoading] = useState(false);

  let loggedInUserInfoDB = useSelector((state) => state.users.userDetails);
  const emailId = useEmailId();
  const notifications = useSelector((state) => state.users.notifications);
  const shownNotificationIds = useRef(null);

  const handleProfile = () => {
    navigate("/account/profile");
  };

  const handleLogout = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const appConfig = localStorage.getItem("appConfig");
      getStore().dispatch({ type: Actions.LOGOUT_USER });

      if (typeof authCtx.logout === "function") {
        await authCtx.logout();
      }
      if (appConfig && !localStorage.getItem("appConfig")) {
        localStorage.setItem("appConfig", appConfig);
      }

      window.location.href = "/login";
    } catch (error) {
      console.error("Logout error:", error);
      const appConfig = localStorage.getItem("appConfig");
      localStorage.clear();
      if (appConfig) {
        localStorage.setItem("appConfig", appConfig);
      }
      window.location.href = "/login";
    }
  };

  const toggleSearch = () => {
    setSearch(!search);
    setValue("");
  };

  // auto focus into input when search is clicked
  useEffect(() => {
    if (searchRef.current) {
      const inputElement = searchRef.current.querySelector("input");
      if (inputElement) inputElement.focus();
    }
  }, [search]);

  useEffect(() => {
    setSearch(false);
    setValue("");
  }, [location.pathname]);

  const searchPaths = [
    "/account/organizations-overview",
    `/account/${params.orgId}/sites-overview`,
    `/account/${params.orgId}/blueprints`,
  ];

  const shouldShowSearch = searchPaths.includes(location.pathname);

  useEffect(() => {
    if (emailId) {
      getStore().dispatch({ type: Actions.API_USER_DETAILS });
      getStore().dispatch({ type: Actions.API_GET_USER_NOTIFICATIONS });
    }
  }, [location.pathname]);

  // bells for backups, WordPress updates and add-ons are written by background jobs minutes
  // after the click, so re-read them every minute while the tab is in front
  const notificationsPollStartedAt = useRef(null);
  useEffect(() => {
    if (!emailId || !showNotifications) {
      return;
    }
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") {
        return;
      }
      // one request at a time; the saga is takeLatest, so a request cancelled by another
      // fetch never reports back: give up on it after two minutes
      if (
        notificationsPollStartedAt.current &&
        Date.now() - notificationsPollStartedAt.current < 120000
      ) {
        return;
      }
      notificationsPollStartedAt.current = Date.now();
      const done = () => {
        notificationsPollStartedAt.current = null;
      };
      getStore().dispatch({
        type: Actions.API_GET_USER_NOTIFICATIONS,
        meta: { onSuccess: done, onError: done },
      });
    }, 60000);
    return () => clearInterval(timer);
  }, [emailId, showNotifications]);

  const orgPath = location.pathname === "/account/organizations-overview";
  const currentPath = location.pathname;
  const placeholder = (() => {
    if (currentPath.includes("organizations-overview")) {
      return "Find your organization";
    } else if (currentPath.includes("sites-overview")) {
      return "Find your site";
    } else if (currentPath.includes("blueprints")) {
      return "Find your blueprint";
    } else {
      return "Search";
    }
  })();

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: Hello,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const organizationView = location?.pathname.includes(
    "/organizations-overview",
  );
  const buyPlan = location?.pathname.includes("/buy-plan");

  useEffect(() => {
    if (loggedInUserInfoDB?.theme) {
      const userTheme = loggedInUserInfoDB.theme;
      setTheme(userTheme);
      document.documentElement.setAttribute("data-theme", userTheme);
      localStorage.setItem("theme", userTheme);
    }
  }, [loggedInUserInfoDB?.theme]);

  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute("data-theme", theme);
      localStorage.setItem("theme", theme);
    }
  }, [theme]);

  const toggleTheme = () => {
    setLoading(true);
    const newTheme = theme === "light" ? "dark" : "light";
    getStore().dispatch({
      type: Actions.API_GET_THEME_DETAILS,
      data: { theme: newTheme },
      meta: {
        onSuccess: () => {
          setTheme(newTheme);
          document.documentElement.setAttribute("data-theme", newTheme);
          localStorage.setItem("theme", newTheme);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      },
    });
  };

  // useEffect(() => {
  //   const newNotifications = notifications?.newNotifications;
  //   if (!newNotifications?.length) return;

  //   if (shownNotificationIds.current === null) {
  //     // First load — seed the set without showing toasts
  //     shownNotificationIds.current = new Set(newNotifications.map((n) => n.id));
  //     return;
  //   }

  //   newNotifications.forEach((n) => {
  //     if (!shownNotificationIds.current.has(n.id)) {
  //       shownNotificationIds.current.add(n.id);
  //       toast(
  //         <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
  //           <div
  //             style={{
  //               width: 36,
  //               height: 36,
  //               borderRadius: "50%",
  //               background: "#d2dff1",
  //               display: "flex",
  //               alignItems: "center",
  //               justifyContent: "center",
  //               flexShrink: 0,
  //             }}
  //           >
  //             <svg
  //               xmlns="http://www.w3.org/2000/svg"
  //               width="18"
  //               height="18"
  //               viewBox="0 0 24 24"
  //               fill="#135fc2"
  //             >
  //               <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  //             </svg>
  //           </div>
  //           <div>
  //             <div
  //               style={{
  //                 fontSize: 13,
  //                 fontWeight: 600,
  //                 color: "#0b0d0e",
  //                 lineHeight: "1.3",
  //                 marginBottom: 3,
  //               }}
  //             >
  //               {n.header}
  //             </div>
  //             <div
  //               style={{
  //                 fontSize: 12,
  //                 fontWeight: 400,
  //                 color: "#636b74",
  //                 lineHeight: "1.4",
  //               }}
  //             >
  //               {n.body}
  //             </div>
  //           </div>
  //         </div>,
  //         {
  //           toastId: `notif-${n.id}`,
  //           autoClose: 5000,
  //           icon: false,
  //           style: {
  //             background: "#ffffff",
  //             border: "1px solid rgba(19, 95, 194, 0.2)",
  //             borderRadius: 8,
  //             boxShadow:
  //               "-4px -4px 8px 0px rgba(19,95,194,0.08), 4px 4px 8px 0px rgba(19,95,194,0.08)",
  //             padding: "12px 14px",
  //             minWidth: 320,
  //           },
  //         },
  //       );
  //     }
  //   });
  // }, [notifications?.newNotifications]);

  const hasPlanData = useSelector((state) => state.users?.hasPlan);

  const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
  const appLogo = appConfig.logo;

  let domain = window.location.hostname;
  domain =
    domain === "localhost" ? process.env.REACT_APP_METADATA_DOMAIN : domain;

  return (
    <>
      <Grid2 className={styles.flexbox}>
        {showLogo && (
          <Grid2
            className={styles.icon}
            onClick={() => {
              if (organizationView || buyPlan) {
                window.location.reload();
              } else if (!hasPlanData) {
                navigate(`/account/buy-plan`);
              } else {
                navigate("/account/organizations-overview");
              }
            }}
          >
            {/* <vendorLogo/> */}
            {/* <Logo /> */}
            <img src={appLogo} alt="App Logo" className={styles.applogo} />
          </Grid2>
        )}
        <Grid2 className={`${styles.navbar} ${orgPath ? styles.width : ""}`}>
          <Grid2>
            {shouldShowSearch && (
              <Grid2
                className={`${styles.searchTrigger} ${
                  search ? styles.hideTrigger : ""
                }`}
                onClick={toggleSearch}
              >
                <SearchIcon className={styles.triggerIcon} />
                <Grid2 className={styles.triggerText}>Search</Grid2>
              </Grid2>
            )}

            <Grid2
              ref={searchRef}
              className={`${styles.searchInputContainer} ${
                search ? styles.showInput : styles.hideTrigger
              }`}
            >
              <SearchIcon className={styles.inputIcon} />
              <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={styles.inputField}
              />
              <ClearIcon
                className={styles.clearIcon}
                onClick={() => {
                  toggleSearch();
                  setValue("");
                }}
              />
            </Grid2>
          </Grid2>
          <Grid2 className={styles.displayFlex}>
            {loggedInUserInfoDB && (
              <Grid2 className={styles.themeToggle} onClick={toggleTheme}>
                {loading ? (
                  <CircularProgress size={24} className={styles.loadingColor} />
                ) : (
                  <>
                    {theme === "light" ? (
                      <Tooltip title="Dark Mode" arrow placement="bottom">
                        <DarkModeOutlinedIcon className={styles.themeIcon} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Light Mode" arrow placement="bottom">
                        <LightModeOutlinedIcon className={styles.themeIcon} />
                      </Tooltip>
                    )}
                  </>
                )}
              </Grid2>
            )}

            {!isMobile && showProfile && (
              <Grid2 className={styles.profileFlexBox}>
                <Grid2 className={styles.hello}>
                  <Lottie options={defaultOptions} height={26} width={26} />
                </Grid2>
                <Grid2 className={styles.header1}>
                  <span className={styles.text}>Hello,</span>{" "}
                  <span className={styles.text}>
                    {loggedInUserInfoDB?.userName
                      ? loggedInUserInfoDB?.userName
                      : "_____"}
                  </span>
                  <Grid2 className={styles.text1}>
                    Welcome back
                    {domain === process.env.REACT_APP_FRONTEND_DOMAIN
                      ? " to Rocon"
                      : ""}
                  </Grid2>
                </Grid2>
              </Grid2>
            )}
            <Grid2 className={styles.profileNof}>
              {showProfile && (
                <Grid2 className={styles.Avatar} onClick={handleProfile}>
                  <Avatar className={styles.profile} />
                </Grid2>
              )}
              {showNotifications && (
                <Grid2 className={styles.Notification}>
                  <Notifications />
                </Grid2>
              )}
            </Grid2>
            {showLogout && (
              <Tooltip title="Logout" arrow placement="bottom">
                <Grid2
                  className={styles.LogoutOutlinedIcon}
                  onClick={handleLogout}
                >
                  <LogoutOutlinedIcon className={styles.logOut} />
                </Grid2>
              </Tooltip>
            )}
          </Grid2>
        </Grid2>
      </Grid2>
      {showLogo && <div className={styles.line} />}
    </>
  );
}

export default NavBar;
